import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useRouter } from '@tanstack/react-router';
import { can } from '@abastece/shared';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';
import { claveTitulo, clavePaso, leccionPorId, pasoInicial, pasosPara, type Leccion, type Paso } from './lecciones.js';
import {
  INSTANTANEA_VACIA,
  leccionesHechas,
  marcarHecha,
  marcarOfrecido,
  tomarInstantanea,
  yaSeOfrecio,
  type Instantanea,
} from './motor.js';
import { LECCIONES } from './lecciones.js';
import { abarcar, BORDE, colocar, type Marco, type Sitio, type Ventana } from './colocar.js';
import { enPractica } from '../lib/practica.js';
import { sesionActual, useSesion } from '../lib/sesion.js';
import { irA } from '../lib/volver.js';

/**
 * El tutorial encima de la aplicación de verdad.
 *
 * It teaches on the real screens, not on pictures of them. Everything dims except the one
 * thing to touch, and the dimming is four solid panels around that thing rather than one
 * translucent sheet over everything — so the gap in the middle is a real hole that real taps
 * fall through, and every tap that lands anywhere else hits a panel and does nothing. That is
 * the whole trick, and it is why somebody cannot get lost: there is exactly one thing on
 * screen that responds.
 *
 * A step ends when the shop changes, not when a button is pressed — see `motor.ts`. So the
 * overlay never has to guess whether somebody finished; it watches for the fact.
 */

// ---------------------------------------------------------------------------
// Estado, fuera de React para que cualquier pantalla pueda arrancar una lección
// ---------------------------------------------------------------------------

interface Estado {
  leccion: Leccion | null;
  indice: number;
  /** The shop as it was when this lesson started. `cuando` compares against it. */
  inicio: Instantanea;
  terminada: boolean;
  /** Steps passed over at the start (`soloSi`): left out of the count, as if not there. */
  saltados: number;
}

let estado: Estado = { leccion: null, indice: 0, inicio: INSTANTANEA_VACIA, terminada: false, saltados: 0 };
const oyentes = new Set<() => void>();
const avisar = () => oyentes.forEach((f) => f());
const poner = (p: Partial<Estado>) => {
  estado = { ...estado, ...p };
  avisar();
};

export function iniciarLeccion(id: string): void {
  const deTodos = leccionPorId(id);
  if (!deTodos) return;
  // Without the steps about what this person's screens do not show them (`Paso.permisos`).
  const sesion = sesionActual();
  const leccion = pasosPara(deTodos, (p) => (sesion ? can(sesion.rol, p) : false));
  // The shop first: it is the baseline every `cuando` compares against, and it says whether the
  // step that sets the shop up is needed. Until then nothing is shown, rather than a step that
  // would vanish a moment later.
  void tomarInstantanea().then((i) => {
    const desde = pasoInicial(leccion, i);
    poner({ leccion, indice: desde, saltados: desde, terminada: false, inicio: i });
  });
}

export const salirDelTutorial = (): void =>
  poner({ leccion: null, indice: 0, terminada: false, saltados: 0 });

export const tutorialActivo = (): boolean => estado.leccion !== null;

function useTutorial(): Estado {
  return useSyncExternalStore(
    (f) => {
      oyentes.add(f);
      return () => oyentes.delete(f);
    },
    () => estado,
    () => estado,
  );
}

// ---------------------------------------------------------------------------
// El recuadro
// ---------------------------------------------------------------------------

/** Breathing room around the highlighted thing, so the ring does not sit on its edge. */
const AIRE = 6;

/** How long the spotlight may sit off screen before the overlay scrolls it back. */
const MS_PERDIDO = 700;

/**
 * The stylesheet turns off every CSS animation for somebody who asked their phone to stop
 * moving things. A smooth scroll is asked for from here instead, so it has to ask too.
 */
const comoDesplazar = (): ScrollBehavior =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

/**
 * What can actually be seen right now.
 *
 * `visualViewport` and not `innerHeight`, so that a keyboard covering the lower half of the
 * screen moves the card up instead of leaving it underneath the keys. Read fresh every frame
 * by the loop below, which is why nothing here listens for a resize.
 */
function ventanaVisible(): Ventana {
  const v = window.visualViewport;
  return v ? { top: v.offsetTop, alto: v.height } : { top: 0, alto: window.innerHeight };
}

/**
 * Skip a render when nothing moved.
 *
 * The measuring loop below runs every frame and used to hand React a brand new object each
 * time, so the overlay re-rendered sixty times a second while sitting perfectly still.
 */
const igualMarco = (a: Marco | null, b: Marco | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height);

const igualSitio = (a: Sitio, b: Sitio): boolean => a.top === b.top && a.altoMax === b.altoMax;

export function Tutorial() {
  const t = useT();
  const { leccion, indice, inicio, terminada, saltados } = useTutorial();
  const router = useRouter();
  const [vista, setVista] = useState<{ marco: Marco | null; sitio: Sitio }>({
    marco: null,
    sitio: { top: BORDE, altoMax: 0 },
  });
  const pasoRef = useRef<Paso | null>(null);
  const tarjetaRef = useRef<HTMLDivElement>(null);
  /** The strip a step that hands the screen over is shown in, measured for the room it needs. */
  const franjaRef = useRef<HTMLDivElement>(null);
  const perdidoDesde = useRef<number | null>(null);

  const paso: Paso | null = leccion ? (leccion.pasos[indice] ?? null) : null;
  pasoRef.current = paso;

  const avanzar = useCallback(() => {
    const l = estado.leccion;
    if (!l) return;
    if (estado.indice + 1 >= l.pasos.length) {
      marcarHecha(l.id);
      poner({ terminada: true });
    } else {
      poner({ indice: estado.indice + 1 });
    }
  }, []);

  // Take the person to the screen the step lives on. Doing this in an effect rather than on
  // the tap means it also happens when they arrive at a step by finishing the previous one.
  useEffect(() => {
    if (!paso?.ruta) return;
    if (router.state.location.pathname === paso.ruta) return;
    void router.navigate({ to: paso.ruta });
  }, [paso, router]);

  /**
   * Measure the target — and the card — every frame.
   *
   * Cheaper than it sounds and far more robust than listening for the half-dozen things that
   * can move an element: a keyboard opening, a list loading, a modal animating, the page
   * scrolling. `apareceEl` is checked here too, in the same pass.
   *
   * The card is measured here as well, because where it can go depends on how tall it is and
   * how tall it is depends on the sentence this particular step happens to carry. Deciding
   * that from a ratio instead is what used to push the instruction off the top of the screen.
   *
   * The first measurement is taken before the step is painted, not a frame after. In between,
   * every new step's card was drawn where the last step's had been, and jumped: on CI's fonts a
   * longer sentence drawn there ran off the bottom of a 320 px phone (`e2e/lecciones-en.mjs`).
   */
  useLayoutEffect(() => {
    if (!paso) {
      setVista({ marco: null, sitio: { top: BORDE, altoMax: 0 } });
      return;
    }
    let vivo = true;
    let cuadro = 0;
    perdidoDesde.current = null;

    /**
     * Bring the spotlight back when it has left the screen.
     *
     * The four panels cover everything except the hole, so once the target scrolls out of
     * view every tap in the window lands on a panel and nothing at all responds — the person
     * is shut out of their own lesson with no way on but «Saltar». It waits a moment first,
     * so it is not wrestling a flick that is still slowing down.
     */
    const vigilarPerdido = (el: HTMLElement | null, marco: Marco | null, ventana: Ventana) => {
      const dentro =
        marco !== null &&
        marco.top + marco.height > ventana.top + BORDE &&
        marco.top < ventana.top + ventana.alto - BORDE;
      if (!el || dentro) {
        perdidoDesde.current = null;
        return;
      }
      const ahora = performance.now();
      perdidoDesde.current ??= ahora;
      if (ahora - perdidoDesde.current < MS_PERDIDO) return;
      perdidoDesde.current = null;
      el.scrollIntoView({ block: 'center', behavior: comoDesplazar() });
    };

    const medir = () => {
      if (!vivo) return;
      const actual = pasoRef.current;
      if (actual === paso) {
        const ventana = ventanaVisible();
        const el = paso.ancla
          ? document.querySelector<HTMLElement>(`[data-guia="${paso.ancla}"]`)
          : null;

        let marco: Marco | null = null;
        if (el) {
          // The thing and whatever opens out of it (`data-guia-desborde`): a search's results hang
          // below its box, and a spotlight that left them out left them under the dimming, where
          // no tap could choose one.
          const r = abarcar(
            [el, ...el.querySelectorAll<HTMLElement>('[data-guia-desborde]')].map((x) => x.getBoundingClientRect()),
          );
          if (r) {
            marco = {
              top: r.top - AIRE,
              left: r.left - AIRE,
              width: r.width + AIRE * 2,
              height: r.height + AIRE * 2,
            };
          }
        }

        vigilarPerdido(el, marco, ventana);

        // `scrollHeight`, not the rendered height: it is the height the card WANTS, so the
        // cap this puts back on it cannot feed into the next decision and oscillate.
        const sitio = colocar(
          marco,
          tarjetaRef.current?.scrollHeight ?? 0,
          ventana,
          !paso.noTapar,
        );
        setVista((v) =>
          igualMarco(v.marco, marco) && igualSitio(v.sitio, sitio) ? v : { marco, sitio },
        );

        if (paso.apareceEl && document.querySelector(paso.apareceEl)) avanzar();
      }
      cuadro = requestAnimationFrame(medir);
    };
    medir();
    return () => {
      vivo = false;
      cancelAnimationFrame(cuadro);
    };
  }, [paso, avanzar]);

  // Scroll the target into view once, when the step begins.
  useEffect(() => {
    if (!paso?.ancla) return;
    const el = document.querySelector<HTMLElement>(`[data-guia="${paso.ancla}"]`);
    el?.scrollIntoView({ block: 'center', behavior: comoDesplazar() });
  }, [paso]);

  // Watch the shop for the change that ends this step.
  useEffect(() => {
    if (!paso?.cuando) return;
    let vivo = true;
    const mirar = async () => {
      const i = await tomarInstantanea();
      if (!vivo) return;
      if (pasoRef.current === paso && paso.cuando?.(i, inicio)) avanzar();
    };
    void mirar();
    const timer = setInterval(() => void mirar(), 600);
    return () => {
      vivo = false;
      clearInterval(timer);
    };
  }, [paso, inicio, avanzar]);

  /*
   * A step that hands the screen to a wizard (`suelta`) says how tall its strip is, for the wizard
   * to leave that much room at the top (`--tutorial-arriba`, read by `Asistente`): drawn over the
   * wizard's question, the strip hid the very thing that says what to do.
   */
  useEffect(() => {
    if (!paso?.suelta) return;
    const raiz = document.documentElement;
    let cuadro = 0;
    let antes = -1;
    const medir = () => {
      const alto = Math.ceil(franjaRef.current?.getBoundingClientRect().height ?? 0);
      if (alto !== antes) {
        antes = alto;
        raiz.style.setProperty('--tutorial-arriba', `${alto}px`);
      }
      cuadro = requestAnimationFrame(medir);
    };
    medir();
    return () => {
      cancelAnimationFrame(cuadro);
      raiz.style.removeProperty('--tutorial-arriba');
    };
  }, [paso]);

  // Steps whose task is the tap itself.
  useEffect(() => {
    if (!paso?.alTocar || !paso.ancla) return;
    const el = document.querySelector<HTMLElement>(`[data-guia="${paso.ancla}"]`);
    if (!el) return;
    const alClic = () => setTimeout(avanzar, 120);
    el.addEventListener('click', alClic);
    return () => el.removeEventListener('click', alClic);
  }, [paso, vista.marco, avanzar]);

  if (!leccion) return <Oferta />;

  if (terminada) {
    return (
      <Sombra>
        <Tarjeta>
          <div className="brinca chip chip-esmeralda mx-auto mb-4 size-14">
            <Ico icono="listo" className="size-7" />
          </div>
          <h2 className="text-center text-xl font-bold">
            {t('tutorial.terminada', { leccion: t(claveTitulo(leccion.id)) })}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-400">{t('tutorial.terminadaDetalle')}</p>
          <button
            type="button"
            className="btn-primario mt-6 w-full"
            onClick={() => {
              salirDelTutorial();
              // Back to the lessons the lesson was begun from, the screens it walked through left
              // behind, so their «Volver» is the way the lessons were opened (`lib/volver.ts`).
              void irA({ to: '/aprender' });
            }}
          >
            <Ico icono="aprender" />
            {t('tutorial.verLecciones')}
          </button>
        </Tarjeta>
      </Sombra>
    );
  }

  if (!paso) return null;

  // A step passed over at the start was never there, as far as the count goes.
  const total = leccion.pasos.length - saltados;
  const numero = indice + 1 - saltados;
  const esperando = Boolean(paso.cuando ?? paso.apareceEl ?? paso.alTocar);
  const texto = t(clavePaso(leccion.id, paso.id));

  const barra = (
    <div className="flex items-center gap-3">
      <span className="rotulo mb-0 shrink-0">{t('tutorial.paso', { n: numero, total })}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-800">
        <div
          className="h-full rounded-full bg-marca-500 transition-all"
          style={{ width: `${(numero / total) * 100}%` }}
        />
      </div>
    </div>
  );

  const controles = (
    <div className="mt-4 flex items-center gap-2">
      <button
        type="button"
        className="btn-texto gap-1 text-xs text-slate-500"
        onClick={salirDelTutorial}
      >
        <Ico icono="cerrar" className="size-3.5" />
        {t('tutorial.salir')}
      </button>
      <span className="flex-1" />
      {esperando ? (
        <button
          type="button"
          className="btn-texto gap-1 text-xs text-slate-500"
          onClick={avanzar}
        >
          <Ico icono="saltar" className="size-3.5" />
          {t('tutorial.saltar')}
        </button>
      ) : (
        <button
          type="button"
          className="btn-primario h-11 gap-1.5 px-6 text-sm"
          onClick={avanzar}
        >
          <Ico icono="marca" className="size-4" />
          {t('tutorial.entendido')}
        </button>
      )}
    </div>
  );

  /*
   * A wizard on screen (`suelta`): it says on each of its screens what to do, so nothing is dimmed
   * and nothing pointed at, and the card is a strip across the top, out of the wizard's way. A
   * card in the middle with the screen dimmed behind it would wait for a tap the dimming takes.
   */
  if (paso.suelta) {
    return (
      <div
        ref={franjaRef}
        className="pointer-events-none fixed inset-x-0 top-0 z-50 px-4 pt-[max(env(safe-area-inset-top),0.5rem)]"
        data-tutorial-franja
      >
        <div key={`${leccion.id}.${paso.id}`} className="llega pointer-events-auto">
          <Tarjeta ref={tarjetaRef} altoMax={Math.round(ventanaVisible().alto * 0.4)}>
            {barra}
            <p className="mt-2 text-sm leading-relaxed">{texto}</p>
            {controles}
          </Tarjeta>
        </div>
      </div>
    );
  }

  // No anchor, or an anchor that is not on screen: say the step in the middle and get out of
  // the way. A tutorial that points at nothing must not also block everything.
  const { marco, sitio } = vista;

  if (!marco) {
    return (
      <Sombra>
        <Tarjeta ref={tarjetaRef}>
          {barra}
          <p className="mt-3 text-base leading-relaxed">{texto}</p>
          {paso.ancla && esperando && (
            <p className="mt-2 text-xs text-slate-500">{t('tutorial.buscando')}</p>
          )}
          {controles}
        </Tarjeta>
      </Sombra>
    );
  }

  const abajo = marco.top + marco.height;

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      {/* Four panels around the target. The gap between them is the only place a tap lands. */}
      <Panel style={{ top: 0, left: 0, right: 0, height: Math.max(marco.top, 0) }} />
      <Panel style={{ top: abajo, left: 0, right: 0, bottom: 0 }} />
      <Panel style={{ top: marco.top, left: 0, width: Math.max(marco.left, 0), height: marco.height }} />
      <Panel style={{ top: marco.top, left: marco.left + marco.width, right: 0, height: marco.height }} />

      {/*
        A halo going out from the thing being pointed at.
        Opacity and scale on a second, identical box, so the ring underneath keeps exactly the
        geometry the tracker measured and the pulse costs the compositor and nothing else.
      */}
      <div
        className="pulso pointer-events-none absolute rounded-xl ring-2 ring-marca-400/60"
        style={{ top: marco.top, left: marco.left, width: marco.width, height: marco.height }}
      />

      <div
        className="pointer-events-none absolute rounded-xl ring-2 ring-marca-400"
        style={{
          top: marco.top,
          left: marco.left,
          width: marco.width,
          height: marco.height,
          boxShadow: '0 0 0 4px rgba(45,212,191,0.25)',
        }}
      />

      {/*
        Keyed by the step, so the card is a new element each time and its entrance replays.
        The panels and the ring deliberately do NOT animate between steps: they are re-measured
        every frame, and a transition on geometry that changes every frame spends the whole
        lesson chasing the page instead of sitting on the thing it is pointing at.
      */}
      <div
        key={`${leccion.id}.${paso.id}`}
        className="llega pointer-events-auto absolute inset-x-0 px-4"
        style={{ top: sitio.top }}
      >
        <Tarjeta ref={tarjetaRef} altoMax={sitio.altoMax}>
          {barra}
          <p className="mt-3 text-base leading-relaxed">{texto}</p>
          {esperando && <p className="mt-2 text-xs text-marca-300">{t('tutorial.hazlo')}</p>}
          {controles}
        </Tarjeta>
      </div>
    </div>
  );
}

/** One of the four blockers. Solid, not translucent: it is doing a job, not a mood. */
const Panel = ({ style }: { style: React.CSSProperties }) => (
  <div className="pointer-events-auto absolute bg-slate-950/80" style={style} />
);

/**
 * The inner flex is not decoration: `items-center` directly on a scrolling box cuts the top
 * off anything taller than the box, so a long message would lose its first lines exactly
 * where it could least afford to. `min-h-full` centres a short card and lets a tall one
 * start at the top and scroll.
 */
const Sombra = ({ children }: { children: React.ReactNode }) => (
  <div className="aparece fixed inset-0 z-50 overflow-y-auto bg-slate-950/85 p-4">
    <div className="flex min-h-full items-center justify-center">{children}</div>
  </div>
);

function Tarjeta({
  children,
  altoMax,
  ref,
}: {
  children: React.ReactNode;
  /** Set when the gap the card sits in is shorter than the card: it scrolls instead. */
  altoMax?: number;
  ref?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      /*
       * `bg-none bg-slate-900` because `.tarjeta` fades to `slate-900/50` at its foot, which
       * is a nice touch on a card sitting on a dark page and a bad one here. When the card
       * has to lie across the spotlight — the one part of the screen that is NOT dimmed — the
       * menu behind it comes through the sentence somebody is trying to read. Being on screen
       * and being readable are not the same promise, and this card owes both.
       */
      className="tarjeta mx-auto w-full max-w-md overflow-y-auto bg-slate-900 bg-none
                 shadow-2xl shadow-black/60 ring-white/10"
      style={altoMax ? { maxHeight: altoMax } : undefined}
    >
      {children}
    </div>
  );
}


// ---------------------------------------------------------------------------
// La oferta
// ---------------------------------------------------------------------------

/**
 * Auto-start happens once per practice session, not once per page load.
 *
 * A module flag alone reset on every refresh, so leaving a lesson and reloading dragged the
 * person straight back into it — the tutorial grabbing the wheel every time they touched the
 * address bar. `sessionStorage` lasts exactly as long as the tab, which is the same lifetime
 * as the practice shop itself.
 */
const CLAVE_ARRANCADO = 'abastece.practica.arrancado';

function yaArrancoAlguna(): boolean {
  try {
    return sessionStorage.getItem(CLAVE_ARRANCADO) === '1';
  } catch {
    // Without storage, never auto-start. Being asked to press a button beats being trapped.
    return true;
  }
}

function anotarArranque(): void {
  try {
    sessionStorage.setItem(CLAVE_ARRANCADO, '1');
  } catch {
    /* Then it does not auto-start, which is the harmless direction. */
  }
}

/**
 * What happens when nobody asked for a tutorial.
 *
 * Two different jobs. In the real shop it offers, once ever, and remembers being turned down —
 * a cashier handed a phone gets taught without anybody remembering to tell her, and an owner
 * who has used the thing for a year is not nagged. Inside the practice shop it does not ask at
 * all; it simply starts the next lesson, because being in the practice shop IS the request.
 */
function Oferta() {
  const t = useT();
  const router = useRouter();
  const sesion = useSesion();
  const practica = enPractica();
  /**
   * Dismissal is React state as well as storage.
   *
   * `marcarOfrecido` writes to localStorage, and localStorage does not re-render anything — so
   * the offer stayed on screen after being accepted, a full-screen panel over the page it had
   * just navigated to, blocking every tap underneath it.
   */
  const [oculto, setOculto] = useState(() => yaSeOfrecio());

  const cerrar = () => {
    marcarOfrecido();
    setOculto(true);
  };

  useEffect(() => {
    if (!practica || !sesion || yaArrancoAlguna()) return;
    anotarArranque();
    const pendiente = LECCIONES.find((l) => !leccionesHechas().has(l.id));
    if (pendiente) iniciarLeccion(pendiente.id);
  }, [practica, sesion]);

  if (practica || !sesion || oculto) return null;

  return (
    <Sombra>
      <Tarjeta>
        <div className="brinca chip chip-marca mx-auto mb-4 size-14">
          <Ico icono="negocio" className="size-7" />
        </div>
        <h2 className="text-center text-xl font-bold">{t('tutorial.oferta.titulo')}</h2>
        <p className="mt-2 text-center text-sm leading-relaxed text-slate-400">
          {t('tutorial.oferta.detalle')}
        </p>
        <button
          type="button"
          className="btn-primario mt-6 w-full"
          onClick={() => {
            cerrar();
            void router.navigate({ to: '/aprender' });
          }}
        >
          <Ico icono="aprender" />
          {t('tutorial.oferta.si')}
        </button>
        <button
          type="button"
          className="btn-texto mt-3 w-full gap-1 text-xs text-slate-500"
          onClick={cerrar}
        >
          <Ico icono="despues" className="size-3.5" />
          {t('tutorial.oferta.no')}
        </button>
      </Tarjeta>
    </Sombra>
  );
}
